import React, { useState, useMemo, useEffect } from 'react';
import { PDFObject } from 'react-pdfobject';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Loader from 'react-loader-spinner';
import axios from 'axios';

import { DEVICES_SIZES, COLORS } from 'apps/web_forms/utils/constants';
import FullscreenIcon from 'apps/web_forms/assets/fullscreen.svg';
import ErrorIcon from 'apps/web_forms/assets/errorgraphic.svg';
import useGlobalSelector from 'apps/web_forms/hooks/useGlobalSelector';

const StyledFilePreview = styled.div`
  width: 100%;
  max-width: 500px;

  a {
    color: black;
    font-weight: 100;
    text-decoration: none;
  }

  a * {
    vertical-align: middle;
  }

  a span {
    margin-right: 0.5em;
  }

  svg {
    width: 1em;
  }

  div#text-preview {
    max-height: 45vh;
    overflow-y: auto;
    padding: 0.5em;
    text-align: left;
    font-size: 0.9em;
  }

  iframe,
  div#pdfobject,
  div#text-preview {
    margin-top: 2em;
    margin-top: 0.5em;
    border: none;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.2), 0 0 20px rgba(0, 0, 0, 0.1);
  }

  div#pdfobject embed {
    min-height: 450px;
  }

  img {
    max-width: 100%;
  }

  .empty-image {
    box-sizing: border-box;
    order: -1;
    width: 100%;
    text-align: center;

    svg {
      width: 80%;
    }

    p {
      padding: 0.4em 0;
    }
  }

  @media (max-width: ${DEVICES_SIZES.tablet}) {
    iframe,
    div#pdf-object,
    div#text-preview {
      height: 65vh;
    }
  }
`;

const ResourcePreview = ({ element }) => {
  const [errorImage, setErrorImage] = useState(false);
  const [textData, setTextData] = useState('');

  const {resourcesMap} = useGlobalSelector();
  const resource = resourcesMap[element.resource_identifier];

  const fileType = useMemo(() => {
    switch (resource.mimeType) {
      case 'application/pdf':
        return 'pdf';
      case 'image/png':
      case 'image/jpeg':
        return 'image';
      case 'text/plain':
        return 'text';
      default:
        return '';
    }
  }, [resource.mimeType]);

  useEffect(() => {
    async function loadText() {
      const response = await axios.get(resource.url, { responseType: 'text' });
      setTextData(response.data);
    }

    if (fileType === 'text') {
      loadText();
    } else {
      setTextData('');
    }
  }, [resource, fileType]);

  if (!resource?.url)
    return (
      <div data-testid="loading">
        <Loader type="Oval" color={COLORS.primary} height="16em" width={100} />
      </div>
    );

  if (fileType === 'text')
    return (
      <StyledFilePreview id={`preview-${element.identifier}`}>
        <a href={resource.url} target="_blank" rel="noreferrer">
          <span>View Fullscreen</span>
          <FullscreenIcon />
        </a>
        <div id="text-preview">
          {textData.split('\n').map((line) => (
            <p>{line}</p>
          ))}
        </div>
      </StyledFilePreview>
    );

  if (fileType === 'pdf')
    return (
      <StyledFilePreview id={`preview-${element.identifier}`}>
        <a href={resource.url} target="_blank" rel="noreferrer">
          <span>View Fullscreen</span>
          <FullscreenIcon />
        </a>
        <PDFObject url={resource.url} />
      </StyledFilePreview>
    );

  if (fileType === 'image' && !errorImage)
    return (
      <StyledFilePreview id={`preview-${element.identifier}`}>
        <a href={resource.url} target="_blank" rel="noreferrer">
          <img
            src={`${resource.url}`}
            onError={() => setErrorImage(true)}
            alt="preview"
          />
        </a>
      </StyledFilePreview>
    );

  if (fileType === 'image' && errorImage)
    return (
      <StyledFilePreview id={`preview-${element.identifier}`}>
        <div className="empty-image">
          <ErrorIcon />
          <p>Image couldn&apos;t be loaded</p>
        </div>
      </StyledFilePreview>
    );

  return <></>;
};

ResourcePreview.propTypes = {
  element: PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    resource_identifier: PropTypes.string,
    hint: PropTypes.string,
  }).isRequired,
};

export default ResourcePreview;
